import {
    createSlice,
    PayloadAction,
    createSelector,
    Action,
    CaseReducers,
    CaseReducer,
    SliceCaseReducers,
    ValidateSliceCaseReducers,
} from '@reduxjs/toolkit';
import createCachedSelector from 're-reselect';
import {
    entitiesToState,
    normalizedEntitiesToState,
    EntityState,
    DataSliceState,
    EntityID,
    IndexedEntities,
    mergeEntities,
} from './entities';
import { keyPath } from '€lib/fp';
import { RootState } from '€slices/types';

export type SliceReducer<T, P = T> = CaseReducer<DataSliceState<T>, { type: string; payload: P }>;

export type ReceiveActionPayload<T> = T[];
export type ReceiveErrorPayload = { error: string };

export type ReceiveActionPayloadOptions = {
    merge?: boolean;
};

export type ReceiveActionPayloadWithOptions<EntityType> = {
    entities: ReceiveActionPayload<EntityType>;
} & ReceiveActionPayloadOptions;

export const defaultRequestData = <State extends DataSliceState<unknown>>(state: State): State => ({
    ...state,
    loading: true,
});

export const defaultReceiveDataSuccess =
    <
        EntityType,
        ResponseType = unknown,
        State extends DataSliceState<EntityType> = DataSliceState<EntityType>,
    >({
        idProp,
        dataProp,
        dataKeyPath,
    }: { idProp?: keyof EntityType; dataProp?: keyof ResponseType; dataKeyPath?: string } = {}) =>
    (
        state: State,
        action: PayloadAction<
            | ResponseType
            | ReceiveActionPayload<EntityType>
            | ReceiveActionPayloadWithOptions<EntityType>
        >,
    ): State => {
        let entities: unknown;
        let actualPayload: unknown = action.payload;
        let options: ReceiveActionPayloadOptions = {};

        if ((action.payload as ReceiveActionPayloadWithOptions<EntityType>).entities) {
            const { entities: $entities, ...$options } =
                action.payload as ReceiveActionPayloadWithOptions<EntityType>;

            actualPayload = $entities;
            options = $options;
        }

        if (dataKeyPath) {
            entities = keyPath<ReceiveActionPayload<EntityType>>(dataKeyPath)(actualPayload);
        } else if (dataProp) {
            entities = (actualPayload as Pick<PayloadAction<ResponseType>, 'payload'>)[
                String(dataProp)
            ];
        } else {
            entities = actualPayload;
        }

        let finalEntities: EntityState<EntityType>;

        if (entities instanceof Array) {
            finalEntities = entitiesToState<EntityType>(idProp || ('id' as keyof EntityType))(
                entities as ReceiveActionPayload<EntityType>,
            );
        } else {
            finalEntities = normalizedEntitiesToState(entities as IndexedEntities<EntityType>);
        }

        if (options.merge !== false) {
            finalEntities = mergeEntities(state, finalEntities);
        }

        return {
            ...state,
            loading: false,
            ...finalEntities,
        };
    };

export const defaultReceiveDataFailure = <State extends DataSliceState<unknown>>(
    state: State,
    action: PayloadAction<ReceiveErrorPayload>,
): State => ({
    ...state,
    loading: false,
    error: action.payload.error,
});

function createDataSlice<
    EntityType,
    ResponseType = unknown,
    SliceState extends DataSliceState<EntityType> = DataSliceState<EntityType>,
    ReducerRootState = RootState,
    AS extends Record<string, Action<any>> = Record<string, Action<any>>,
    CR extends SliceCaseReducers<SliceState> = SliceCaseReducers<SliceState>,
>({
    name,
    idProp,
    reducers,
    extraReducers,
    dataProp,
    dataKeyPath,
    extraInitialState,
}: {
    name: string;
    idProp?: keyof EntityType;
    reducers?: ValidateSliceCaseReducers<SliceState, CR>;
    extraReducers?: CaseReducers<SliceState, AS>;
    dataProp?: keyof ResponseType;
    dataKeyPath?: string;
    extraInitialState?: Partial<SliceState>;
}) {
    const initialState = {
        ...extraInitialState,
        byId: {},
        allIds: [] as EntityID[],
        loading: false,
        error: undefined,
    } as SliceState;

    const { actions, reducer } = createSlice({
        name,
        initialState,
        reducers: {
            requestData: defaultRequestData,
            receiveDataSuccess: defaultReceiveDataSuccess<EntityType, ResponseType, SliceState>({
                idProp,
                dataProp,
                dataKeyPath,
            }),
            receiveDataFailure: defaultReceiveDataFailure,
            reset: () => initialState,
            ...reducers,
        },
        extraReducers,
    });

    const loading = (state: ReducerRootState) =>
        (state[name] as DataSliceState<EntityType>).loading;
    const indexed = (state: ReducerRootState) => (state[name] as DataSliceState<EntityType>).byId;
    const allIds = (state: ReducerRootState) => (state[name] as DataSliceState<EntityType>).allIds;

    const all = createSelector(indexed, Object.values);

    const getById = createCachedSelector(
        indexed,
        (_state: ReducerRootState, id: EntityID) => id,
        ($byId, id): EntityType => $byId[String(id)],
    )((_state, id) => id);

    const selectors = {
        loading,
        indexed,
        allIds,
        all,
        getById,
    };

    return {
        name,
        actions,
        reducer,
        selectors,
    };
}

export default createDataSlice;
